import type { Locale } from '../context/LocaleContextProvider'
import { cookieService } from '../services/CookiesService'

/**
 * The language a visitor picked with the header toggle. Only the toggle sets it: never browser language or
 * location, so crawlers (which never click it) always get the page they asked for.
 */
export const LANGUAGE_PREFERENCE_COOKIE = 'bwll_locale'

const PREFERENCE_DAYS = 365
const LOCALES: Locale[] = ['en-CA', 'fr-CA']

export const saveLanguagePreference = (locale: Locale) =>
  cookieService.setCookie({
    key: LANGUAGE_PREFERENCE_COOKIE,
    value: locale,
    cookieAttributes: { expires: PREFERENCE_DAYS },
  })

export const readLanguagePreference = (): Locale | null => {
  const value = cookieService.getCookie(LANGUAGE_PREFERENCE_COOKIE)
  return LOCALES.includes(value as Locale) ? (value as Locale) : null
}

interface IRedirectInput {
  preference: Locale | null
  locale: Locale
  alternates: Partial<Record<Locale, string>>
}

/**
 * @description the French page to send a visitor to, or null to stay. Only a saved French choice redirects,
 * only from an English page, and only when that page has a French version.
 * @example getFrenchRedirect({ preference: 'fr-CA', locale: 'en-CA', alternates: { 'fr-CA': '/fr/credit-cards' } })
 * => '/fr/credit-cards'
 */
export const getFrenchRedirect = ({ preference, locale, alternates }: IRedirectInput): string | null => {
  if (preference !== 'fr-CA' || locale !== 'en-CA') return null
  return alternates['fr-CA'] ?? null
}

/**
 * @description the inline script that redirects a first page load, before the English page renders. It's
 * rendered only on English pages that have a French version, with that path built in. Iframes are skipped
 * (the Contentful live preview runs the site in one).
 */
export const frenchRedirectScript = (frenchPath: string) =>
  `(function(){try{if(window.self!==window.top)return;` +
  `var m=document.cookie.match(/(?:^|; )${LANGUAGE_PREFERENCE_COOKIE}=([^;]*)/);` +
  `if(!m||decodeURIComponent(m[1])!=='fr-CA')return;` +
  `window.location.replace(${JSON.stringify(frenchPath).replace(
    /</g,
    '\\u003c',
  )}+window.location.search+window.location.hash)` +
  `}catch(e){}})()`
